import { useMemo, useState } from "react";
import { Topbar } from "../components/Topbar";
import { StatusBadge } from "../components/StatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  DOCUMENTS,
  PROPERTIES,
  REQUIREMENT_TYPES,
  REQ_STATUS_LABELS,
  REQ_STATUS_TONE,
  getProperty,
} from "../data/mockData";
import { Download, FileText, Search } from "lucide-react";
import { toast } from "sonner";

export default function Documents() {
  const [q, setQ] = useState("");
  const [propertyId, setPropertyId] = useState("All");
  const [type, setType] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const filtered = useMemo(
    () =>
      DOCUMENTS.filter((d) => {
        if (propertyId !== "All" && d.propertyId !== propertyId) return false;
        if (type !== "All" && d.type !== type) return false;
        if (statusFilter !== "All" && d.status !== statusFilter) return false;
        if (q && !d.name.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
      }),
    [q, propertyId, type, statusFilter]
  );

  return (
    <>
      <Topbar
        title="Document vault"
        subtitle="Central library — licences, insurance certificates, safety records"
      />

      <div className="px-6 lg:px-8 py-8 space-y-6">
        <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-2 rounded-sm border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3">
              <Search size={14} className="text-slate-400" />
              <input
                data-testid="doc-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search documents"
                className="bg-transparent outline-none text-sm py-2 w-full text-slate-800 dark:text-slate-200 placeholder:text-slate-400"
              />
            </div>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger data-testid="doc-filter-property" className="h-9 text-xs">
                <SelectValue placeholder="Property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All properties</SelectItem>
                {PROPERTIES.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="doc-filter-type" className="h-9 text-xs">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All types</SelectItem>
                {[...new Set(REQUIREMENT_TYPES.map((r) => r.category))].map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="doc-filter-status" className="h-9 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All statuses</SelectItem>
                <SelectItem value="current">Current</SelectItem>
                <SelectItem value="expiring">Renewal approaching</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Showing {filtered.length} of {DOCUMENTS.length} documents
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="documents-table">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/40 text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  <th className="py-3 px-6 text-left">Document</th>
                  <th className="py-3 px-4 text-left">Property</th>
                  <th className="py-3 px-4 text-left">Type</th>
                  <th className="py-3 px-4 text-left">Uploaded</th>
                  <th className="py-3 px-4 text-left">Expiry</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-sm text-slate-500">
                      No documents match the current filters.
                    </td>
                  </tr>
                )}
                {filtered.map((d) => {
                  const p = getProperty(d.propertyId);
                  return (
                    <tr
                      key={d.id}
                      className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-slate-500 shrink-0" />
                          <span className="text-slate-900 dark:text-slate-100">{d.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{p?.name}</td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{d.type}</td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                        {d.uploadedAt}
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                        {d.expiryDate || "—"}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge tone={REQ_STATUS_TONE[d.status]}>
                          {REQ_STATUS_LABELS[d.status]}
                        </StatusBadge>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          data-testid={`doc-vault-download-${d.id}`}
                          onClick={() => toast.success(`Downloading ${d.name}`)}
                          className="h-8 w-8 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          <Download size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
